import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FileTextIcon, MailIcon, PrinterIcon, RotateCcwIcon, UserIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchOrder } from '../adminApi';
import { useAdmin } from '../AdminContext';
import { PageHeader } from '../ui/PageHeader';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { MissingItem } from '../ui/MissingItem';
import { LoadFailed } from '../ui/LoadState';
import { Spinner } from '../../ui/Spinner';
import { btnDestructive, btnSecondary } from '../ui/styles';
import { paymentLabel, received } from '../inbox/inboxRules';
import { RefundForm } from './RefundForm';
import { CancelForm } from './CancelForm';
import { OrderTimeline } from './OrderTimeline';
import { FulfilmentPanel } from './FulfilmentPanel';
import { creditNumber, printCreditNote, printOrderInvoice, printOrderPackingSlip } from './orderDocuments';
import { channelLabel, orderBadge, orderNumber, refundLabel, vatBreakdown } from './orderLabels';
import { money } from '../../core/format/currency';
import { cancelProblem, paymentMethodLabel, refundableCents } from '../../core/rules/revenue';
import { formatStreet } from '../../core/rules/contact';
import { trackingUrl } from '../../core/rules/tracking';
import { dayShort } from '../../core/format/dates';
import type { AdminOrder } from '../../core/types/adminRequests';
import type { RequestOptions } from '../../core/types/backend';
type Action = 'refund' | 'cancel' | null;

/**
 * Eén bestelling: wat erin zat (met de btw zoals verkocht), het adres, de betaling, de tijdlijn en
 * de terugbetalingen. Terugbetalen en annuleren wachten altijd op de server (transactiescherm).
 */
export function OrderDetail() {
  const {
    id = ''
  } = useParams();
  // Vanuit de lijst: terug met dezelfde filters. Vanuit de inbox: terug naar de inbox.
  const {
    state
  } = useLocation() as {
    state: {
      from?: string;
      back?: {
        to: string;
        label: string;
      };
    } | null;
  };
  const back = state?.back ?? {
    to: `/be/bestellingen${state?.from ?? ''}`,
    label: 'Alle bestellingen'
  };
  const load = useCallback((opts: RequestOptions) => fetchOrder(id, opts), [id]);
  const {
    data: order,
    setData,
    status,
    reload,
    refresh
  } = useLatestLoad(load);
  const [action, setAction] = useState<Action>(null);
  if (status === 'loading' && !order) return <div role="status" className="flex items-center gap-2 py-16 text-[14px] text-muted" data-id="element-3374" data-mp="23i43w003lw8g">
        <Spinner data-id="element-3375" data-mp="1yngoqrqzie6j" /> Bestelling laden…
      </div>;
  if (status === 'error' && !order) return <LoadFailed title="Deze bestelling kon niet laden" onRetry={reload} data-id="element-3376" data-mp="1todkh9itwbzm" />;
  if (!order) return <MissingItem title="Bestelling niet gevonden" back={back} data-id="element-3377" data-mp="1pmsca1h8olvr" />;
  const done = (next: AdminOrder, message: string) => {
    setData(next);
    setAction(null);
    toast.success(message);
  };
  return <OrderView order={order} back={back} action={action} setAction={setAction} onRefunded={next => done(next, 'Terugbetaald. De klant krijgt een mail van Mollie.')} onCancelled={next => done(next, 'Bestelling geannuleerd')} onFailed={refresh} onFulfilled={refresh} data-id="element-3378" data-mp="3jwm9ey7hxcga" />;
}
type ViewProps = {
  order: AdminOrder;
  back: {
    to: string;
    label: string;
  };
  action: Action;
  setAction: (a: Action) => void;
  onRefunded: (o: AdminOrder) => void;
  onCancelled: (o: AdminOrder) => void;
  onFailed: () => void;
  onFulfilled: () => void;
};
function OrderView({
  order: o,
  back,
  action,
  setAction,
  onRefunded,
  onCancelled,
  onFailed,
  onFulfilled
}: ViewProps) {
  const {
    site,
    index
  } = useAdmin();
  const badge = orderBadge(o);
  const paid = o.paymentStatus === 'betaald';
  const left = refundableCents(o);
  const canRefund = paid && left > 0 && !o.cancelledAt;
  const canCancel = cancelProblem(o) === null;
  const vat = vatBreakdown(o);
  const a = o.address;

  // Het formulier opent onder de regels; op de telefoon valt dat buiten beeld.
  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (action) formRef.current?.scrollIntoView({
      block: 'nearest'
    });
  }, [action]);
  const invoice = async () => {
    if (!(await printOrderInvoice(o, site.salon, dayShort(o.placedAt.slice(0, 10))))) toast.error('Factuur openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  const slip = async () => {
    if (!(await printOrderPackingSlip(o, index.productById, site.salon))) toast.error('Pakbon afdrukken lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <div data-id="element-3379" data-mp="2yfghu17viw40">
      <PageHeader title={o.number ? `Bestelling ${o.number}` : 'Bestelling zonder nummer'} description={`${o.customerName || o.email} · ${received(o.placedAt)} · ${channelLabel[o.channel]} · ${money(o.totalCents)}`} back={back} actions={<>
            <Badge tone={badge.tone} data-id="element-3381" data-mp="3r1x16kfbpjwb">{badge.label}</Badge>
            {paid && <button type="button" onClick={() => void invoice()} className={btnSecondary} data-id="element-3382" data-mp="3r03u540ssxns">
                <FileTextIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3383" data-mp="3vyx78vc6amnf" />
                Factuur
              </button>}
            {paid && !o.cancelledAt && <button type="button" onClick={() => void slip()} className={btnSecondary} data-id="element-3384" data-mp="3m10rfq8aj3iz">
                <PrinterIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3385" data-mp="38a49ctbzq4q6" />
                Pakbon
              </button>}
          </>} data-id="element-3380" data-mp="2yfoupt3zy1f7" />

      <div className="grid gap-6 lg:grid-cols-3" data-id="element-3386" data-mp="2yeanukgan0w9">
        <div className="space-y-6 lg:col-span-2" data-id="element-3387" data-mp="2ybesn3vtyqbi">
          <FulfilmentPanel order={o} onChanged={onFulfilled} data-id="element-3388" data-mp="33d43ojj1z3zl" />
          <Panel title="Wat er besteld is" flush data-id="element-3389" data-mp="2pctcffdtzndw">
            <table className="w-full text-[14px]" data-id="element-3390" data-mp="2pd32f2yvlujl">
              <caption className="sr-only" data-id="element-3391" data-mp="2uc5wxth8cidb">Bestelregels met btw</caption>
              <thead className="text-[12px] text-muted" data-id="element-3392" data-mp="2udgyrnr47g1s">
                <tr className="border-b border-line" data-id="element-3393" data-mp="zw7k7e9v7qv1">
                  <th scope="col" className="px-4 py-2 text-left font-normal sm:px-5" data-id="element-3394" data-mp="v4peye0t7449">Product</th>
                  <th scope="col" className="px-2 py-2 text-right font-normal" data-id="element-3395" data-mp="q5me75rv3k23">Aantal</th>
                  <th scope="col" className="hidden px-2 py-2 text-right font-normal sm:table-cell" data-id="element-3396" data-mp="q39zd1hehpt9">Btw</th>
                  <th scope="col" className="px-4 py-2 text-right font-normal sm:px-5" data-id="element-3397" data-mp="1iubites4m7il">Bedrag</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line" data-id="element-3398" data-mp="1dw9uaebxlfzq">
                {o.lines.map(l => <tr key={l.productId} data-id="element-3399" data-mp="19skcc7prhwno">
                    <td className="px-4 py-3 text-ink sm:px-5" data-id="element-3400" data-mp="19vpxxcz6mgen">
                      {l.name}
                      <span className="block text-[12px] text-muted sm:hidden" data-id="element-3401" data-mp="14wmt9tn46c77">btw {l.vatRate}%</span>
                    </td>
                    <td className="px-2 py-3 text-right tabular-nums" data-id="element-3402" data-mp="3pl15ia63ywad">{l.qty}</td>
                    <td className="hidden px-2 py-3 text-right tabular-nums text-muted sm:table-cell" data-id="element-3403" data-mp="3pn42xf9lbrp4">{l.vatRate}%</td>
                    <td className="px-4 py-3 text-right tabular-nums text-ink sm:px-5" data-id="element-3404" data-mp="2a1k08b3taz9d">{money(l.lineTotalCents)}</td>
                  </tr>)}
              </tbody>
            </table>
            <dl className="space-y-1.5 border-t border-line px-4 py-4 text-[13px] sm:px-5" data-id="element-3405" data-mp="2itvfd0th70a1">
              <div className="flex justify-between gap-4" data-id="element-3406" data-mp="2nt6y2fdekfu9">
                <dt className="text-muted" data-id="element-3407" data-mp="2nsowd37z19ey">Verzending (btw {o.shippingVatRate}%)</dt>
                <dd className="tabular-nums text-ink" data-id="element-3408" data-mp="2sr01j0nbmvqu">{o.shippingCents ? money(o.shippingCents) : 'Gratis'}</dd>
              </div>
              {vat.map(v => <div key={v.vatRate} className="flex justify-between gap-4" data-id="element-3409" data-mp="31thqhmud22hj">
                  <dt className="text-muted" data-id="element-3410" data-mp="31qn3bnjyq3kf">Waarvan btw {v.vatRate}%</dt>
                  <dd className="tabular-nums text-muted" data-id="element-3411" data-mp="13f69ys1np0mr">{money(v.vatCents)}</dd>
                </div>)}
              <div className="flex justify-between gap-4 pt-1.5 text-[15px]" data-id="element-3412" data-mp="17grumzkfmj7i">
                <dt className="text-ink" data-id="element-3413" data-mp="ogvhtgawi6tw">Totaal</dt>
                <dd className="tabular-nums text-ink" data-id="element-3414" data-mp="jk52iv6x7f9j">{money(o.totalCents)}</dd>
              </div>
              {o.voucherCents > 0 && <div className="flex justify-between gap-4" data-id="element-3415" data-mp="xai5sk8va5sj">
                  <dt className="text-muted" data-id="element-3416" data-mp="t96feci65gja">Betaald met cadeaubon (gaat niet via Mollie terug)</dt>
                  <dd className="shrink-0 tabular-nums text-ink" data-id="element-3417" data-mp="5j2dc9kmpoue">{money(o.voucherCents)}</dd>
                </div>}
              {o.refunds.map(r => <div key={r.id} className="flex justify-between gap-4" data-id="element-3418" data-mp="ekincg7j7oy3">
                  <dt className="min-w-0 text-muted" data-id="element-3419" data-mp="eltsajcpr2qs">
                    {refundLabel(r)} {received(r.at)} · <span className="text-ink" data-id="element-3420" data-mp="9jw3l9ljnfqg">{r.reason}</span>
                    {r.restocked && <span data-id="element-3421" data-mp="1pynqx83u1wsw"> · terug op voorraad</span>}
                    {r.voucherRestoredCents ? <span data-id="element-3422" data-mp="1l1dwfe1l6uy7"> · {money(r.voucherRestoredCents)} terug op cadeaubon</span> : null}
                    <button type="button" onClick={() => void printCreditNote(o, r, site.salon).then(ok => {
                  if (!ok) toast.error('Creditnota openen lukte niet', {
                    description: 'Probeer het opnieuw of gebruik een andere browser.'
                  });
                })} className="ml-2 text-ink underline decoration-line underline-offset-4 hover:decoration-ink" data-id="element-3423" data-mp="1th3f4fqa6usr">
                      Creditnota {creditNumber(o, r)}
                    </button>
                  </dt>
                  <dd className="shrink-0 tabular-nums text-clay" data-id="element-3424" data-mp="22h9sm1lzburx">−{money(r.amountCents)}</dd>
                </div>)}
            </dl>
            {(o.gift || o.code) && <p className="border-t border-line px-4 py-3 text-[13px] text-muted sm:px-5" data-id="element-3425" data-mp="2v6ozcf6m31i1">
                {o.gift && <>
                    Cadeau erbij: <span className="text-ink" data-id="element-3426" data-mp="2zzi7ze57atze">{o.gift}</span>
                  </>}
                {o.gift && o.code && ' · '}
                {o.code && <>
                    Code: <span className="text-ink" data-id="element-3427" data-mp="1j0sywrfixtuc">{o.code}</span>
                  </>}
              </p>}
          </Panel>

          <div ref={formRef} className="scroll-mb-24" data-id="element-3428" data-mp="1nyue319tx90j">
            {action === 'refund' ? <RefundForm order={o} onDone={onRefunded} onFailed={onFailed} onClose={() => setAction(null)} data-id="element-3429" data-mp="199yabget2i5q" /> : action === 'cancel' ? <CancelForm order={o} onDone={onCancelled} onFailed={onFailed} onClose={() => setAction(null)} data-id="element-3430" data-mp="1e3kgqqudlpt3" /> : (canRefund || canCancel) && <div className="flex flex-wrap items-center gap-2" data-id="element-3431" data-mp="14c691vs9tjov">
                  {canRefund && <button type="button" onClick={() => setAction('refund')} className={btnSecondary} data-id="element-3432" data-mp="qcx27mpyg1xk">
                      <RotateCcwIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3433" data-mp="vdb1af9kw1jg" />
                      Terugbetalen
                    </button>}
                  {canCancel && <button type="button" onClick={() => setAction('cancel')} className={`${btnDestructive} sm:ml-auto`} data-id="element-3434" data-mp="6sps7q21f1h2">
                      <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3435" data-mp="gnzzas7alz1p" />
                      Bestelling annuleren
                    </button>}
                </div>}
          </div>
          {!canCancel && !o.cancelledAt && paid && o.fulfilment !== 'nieuw' && action === null && <p className="-mt-3 text-[12px] text-muted" data-id="element-3436" data-mp="14dkf5285gg6r">Al ingepakt of verzonden: annuleren kan niet meer, terugbetalen wel.</p>}
        </div>

        <div className="space-y-6" data-id="element-3437" data-mp="1deaa63pl62jf">
          <Panel title="Klant" data-id="element-3438" data-mp="1dfbmscm92x39">
            <p className="text-[14px] text-ink" data-id="element-3439" data-mp="1dcfmwiqudyc6">{o.customerName || '—'}</p>
            <p className="break-all text-[13px] text-muted" data-id="element-3440" data-mp="19f1hz3a72cpz">{o.email}</p>
            <address className="mt-3 text-[13px] not-italic leading-snug text-ink" data-id="element-3441" data-mp="1ghg2gakaaj4w">
              {`${a.firstName} ${a.lastName}`.trim() || o.customerName}
              <br data-id="element-3442" data-mp="1qeks4kdc8wqg" />
              {formatStreet(a)}
              <br data-id="element-3443" data-mp="1qdsxbbrkf318" />
              {a.postcode} {a.city}
              {a.country === 'BE' && ' · België'}
            </address>
            {o.skinNote && <p className="mt-3 rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-3444" data-mp="1z74u9usu9jg1">
                <span className="block text-[12px] text-muted" data-id="element-3445" data-mp="245zacozf8twg">Notitie voor jou</span>“{o.skinNote}”
              </p>}
            <div className="mt-4 flex flex-wrap gap-2" data-id="element-3446" data-mp="294ss4tlqr72f">
              {o.customerId ? <Link to={`/be/klanten/${o.customerId}?tab=bestellingen`} className={btnSecondary} data-id="element-3447" data-mp="2e4ot0jz56idr">
                  <UserIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3448" data-mp="ert5igiy1cln" />
                  Dossier
                </Link> : <span className="text-[12px] text-muted" data-id="element-3449" data-mp="js8iv8386k3m">Gastbestelling</span>}
              <a href={`mailto:${o.email}?subject=${encodeURIComponent(`Je bestelling ${orderNumber(o)}`)}`} className={btnSecondary} data-id="element-3450" data-mp="2x2ibj70hmfgu">
                <MailIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3451" data-mp="2o7u4bbbcqnpe" />
                Mail
              </a>
            </div>
          </Panel>

          <Panel title="Betaling" data-id="element-3452" data-mp="2jatxuzalbixr">
            <dl className="space-y-1.5 text-[13px]" data-id="element-3453" data-mp="3fyzylb4e5r6r">
              <div className="flex justify-between gap-4" data-id="element-3454" data-mp="3fygt4tvxgv9m">
                <dt className="text-muted" data-id="element-3455" data-mp="3bxer65amm53y">Status</dt>
                <dd className="text-ink" data-id="element-3456" data-mp="3bvk2qh05buv4">{paymentLabel[o.paymentStatus]}</dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-3457" data-mp="370od1nidtjl6">
                <dt className="text-muted" data-id="element-3458" data-mp="31yfhqki1e5fw">Methode</dt>
                <dd className="text-ink" data-id="element-3459" data-mp="1vjeud372uvkx">{paymentMethodLabel[o.paymentMethod]}</dd>
              </div>
              {o.refundedCents > 0 && <div className="flex justify-between gap-4" data-id="element-3460" data-mp="1qjaar4avhkqh">
                  <dt className="text-muted" data-id="element-3461" data-mp="3eh1bbitbfgxi">Nog terug te betalen</dt>
                  <dd className="tabular-nums text-ink" data-id="element-3462" data-mp="3efq7jizve96e">{money(left)}</dd>
                </div>}
              {o.tracking && <div className="flex justify-between gap-4" data-id="element-3463" data-mp="35gv4gfs4hf7b">
                  <dt className="text-muted" data-id="element-3464" data-mp="35e8nxqn97pcy">Track & trace</dt>
                  <dd data-id="element-3465" data-mp="3ag7qiyb66i8u">
                    <a href={trackingUrl(o.tracking)} target="_blank" rel="noopener noreferrer" className="text-ink underline underline-offset-2" data-id="element-3466" data-mp="3afo69ay1rhrn">
                      {o.tracking.carrier} {o.tracking.code}
                      <span className="sr-only" data-id="element-3467" data-mp="5z3u0uwdtmke"> (opent in een nieuw tabblad)</span>
                    </a>
                  </dd>
                </div>}
            </dl>
          </Panel>

          <Panel title="Tijdlijn" data-id="element-3468" data-mp="3t80hyoalbveg">
            <OrderTimeline events={o.timeline} data-id="element-3469" data-mp="2cr4p63er879m" />
          </Panel>
        </div>
      </div>
    </div>;
}